import lodashSet from 'lodash.set';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/redux/store';
import { questionnairesApi } from 'app/services/questionnaires';

export type TPleaseProvideDetailsOfAssetsSoldDuringYear =
  TPleaseProvideDetailsOfAssetsSoldDuringYearItem[];

export type TPleaseProvideDetailsOfAssetsSoldDuringYearItem = {
  item: Nullable<string>;
  saleProceeds: Nullable<string>;
  currency: Nullable<string>;
  costBasis: Nullable<string>;
  costBasisCurrency: Nullable<string>;
  dateAquired: Nullable<string>;
  dateSold: Nullable<string>;
};

export type TPleaseEnterInformationBelow = TPleaseEnterInformationBelowItem[];

export type TPleaseEnterInformationBelowItem = {
  security: Nullable<string>;
  purchaseDate: Nullable<string>;
  saleDate: Nullable<string>;
  costBasis: Nullable<string>;
  currency: Nullable<string>;
  proceeds: Nullable<string>;
};

type FieldArrayKeys =
  | 'pleaseProvideDetailsOfAssetsSoldDuringYear'
  | 'pleaseEnterInformationBelow';

export interface InvestmentsStateValues {
  didYouSellAnySecuritiesieStocksBondsMutualFundsCryptocurrencyOptionsEtcDuringTheFilingYear: Nullable<string>;
  doYouHaveForm1099BAndorBrokerStatementsShowingTheTransction: Nullable<string>;
  pleaseProvideDetailsOfAssetsSoldDuringYear: TPleaseProvideDetailsOfAssetsSoldDuringYear;
  ifYouHaveAnExcelSpreadsheetWithTheTransactionsYouCanUploadItInsteadOfCompletingTheTableAbove: Nullable<string>;
  uploadForm1099BOrForeignEquivalent: Nullable<string>;
  didYouCarryOutSalesOfSecuritiesWithAUsFinancialInstitution: Nullable<string>;
  pleaseUploadForm1099B: Nullable<string>;
  didYouCarryOutSalesOfSecuritiesWithAnonUsFinancialInstitiution: Nullable<string>;
  pleaseEnterInformationBelow: TPleaseEnterInformationBelow;
  pleaseAlsoUploadTheYearEndBrokerageStatementForForeignieNonUsTransactions: Nullable<string>;
  doYouHaveInvestmentsInANonUsPooledInvestmentFundsEGMutualFundsUnitTrustsEtc: Nullable<string>;
  isTheTotalValueOfTheInvestmentOver25000Or50000IfYouFileJointly: Nullable<string>;
  didYouMakeAnySalesInTheFundsDuringYear: Nullable<string>;
  KIsTheMinimumAmountForIndividualPficReporting50KIsTheMinimumAmountForSpousesFilingJointlyIfYourTotalNonUsMutualFundsAreBelowThatAmountAndYouDidNotSellAnyFundsThisYearYouDoNotNeedToFileForm8621: Nullable<string>;
  howManyNonUsMutualFundinvestmentAccountsDidYouHave: Nullable<string>;
  uploadAnnualStatementForLast3YearsInWhichTheFundWasHeldshowingYearEndBalance: Nullable<string>;
  proceedscurrency: Nullable<string>;
}

export interface InvestmentsState {
  values: InvestmentsStateValues;
}

const initialState: InvestmentsState = {
  values: {
    didYouSellAnySecuritiesieStocksBondsMutualFundsCryptocurrencyOptionsEtcDuringTheFilingYear:
      null,
    doYouHaveForm1099BAndorBrokerStatementsShowingTheTransction: null,
    pleaseProvideDetailsOfAssetsSoldDuringYear: [
      {
        item: null,
        saleProceeds: null,
        currency: null,
        costBasis: null,
        costBasisCurrency: null,
        dateAquired: null,
        dateSold: null,
      },
    ],

    ifYouHaveAnExcelSpreadsheetWithTheTransactionsYouCanUploadItInsteadOfCompletingTheTableAbove:
      null,
    uploadForm1099BOrForeignEquivalent: null,
    didYouCarryOutSalesOfSecuritiesWithAUsFinancialInstitution: null,
    pleaseUploadForm1099B: null,
    didYouCarryOutSalesOfSecuritiesWithAnonUsFinancialInstitiution: null,
    pleaseEnterInformationBelow: [
      {
        security: null,
        purchaseDate: null,
        saleDate: null,
        costBasis: null,
        currency: null,
        proceeds: null,
      },
    ],
    pleaseAlsoUploadTheYearEndBrokerageStatementForForeignieNonUsTransactions:
      null,
    doYouHaveInvestmentsInANonUsPooledInvestmentFundsEGMutualFundsUnitTrustsEtc:
      null,
    isTheTotalValueOfTheInvestmentOver25000Or50000IfYouFileJointly: null,
    didYouMakeAnySalesInTheFundsDuringYear: null,
    KIsTheMinimumAmountForIndividualPficReporting50KIsTheMinimumAmountForSpousesFilingJointlyIfYourTotalNonUsMutualFundsAreBelowThatAmountAndYouDidNotSellAnyFundsThisYearYouDoNotNeedToFileForm8621:
      null,
    howManyNonUsMutualFundinvestmentAccountsDidYouHave: null,
    uploadAnnualStatementForLast3YearsInWhichTheFundWasHeldshowingYearEndBalance:
      null,
    proceedscurrency: null,
  },
};

const investmentsSlice = createSlice({
  name: 'investments',
  initialState,
  reducers: {
    update(state, action: PayloadAction<InvestmentsStateValues>) {
      state.values = JSON.parse(JSON.stringify(action.payload));
    },
    updateKey(
      state,
      action: PayloadAction<{
        key:
          | keyof InvestmentsStateValues
          | keyof TPleaseProvideDetailsOfAssetsSoldDuringYearItem
          | keyof TPleaseEnterInformationBelowItem;
        value: any;
      }>,
    ) {
      lodashSet(state, `values.${action.payload.key}`, action.payload.value);
    },
    updateFieldArrayKey(
      state,
      action: PayloadAction<{
        key:
          | keyof InvestmentsStateValues
          | keyof TPleaseProvideDetailsOfAssetsSoldDuringYearItem
          | keyof TPleaseEnterInformationBelowItem;
        value: any;
      }>,
    ) {
      const fieldArray = action.payload.value.map((field: any) => ({
        ...field,
      }));
      if (
        action.payload.key === 'pleaseProvideDetailsOfAssetsSoldDuringYear' ||
        action.payload.key === 'pleaseEnterInformationBelow'
      ) {
        state.values[action.payload.key] = fieldArray;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      questionnairesApi.endpoints.getQuestionnaireById.matchFulfilled,
      (state, { payload }) => {
        state.values = payload.questionnaire.data.taxQuestionnaire
          ? payload.questionnaire.data.taxQuestionnaire.income.investments
              .values
          : initialState.values;
      },
    );
  },
});

export const { update, updateKey, updateFieldArrayKey } =
  investmentsSlice.actions;
export default investmentsSlice.reducer;

export const selectInvestments = (state: RootState) =>
  state.taxQuestionnaire.income.investments;
